import React, { useState, useEffect, useRef } from 'react';
import { Link, useLocation } from 'react-router-dom';
import axios from 'axios';
import Icon from '../components/dashboard/DashboardIcons';
import { coachNavigation, getCoachRouteTitle } from '../config/coachNavigation';
import { useTasyarBrand } from '../lib/tasyarBrand';
import { NotificationBell } from '../NotificationBanner';
import CoachMobileBottomNav from './CoachMobileBottomNav';
import CoachMobileMoreSheet from './CoachMobileMoreSheet';
import CoachFooter from './CoachFooter';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const COLLAPSE_KEY = 'coach_sidebar_collapsed';

const isActive = (pathname, href) => pathname === href || pathname.startsWith(href + '/');

const SidebarLink = ({ item, active, collapsed }) => (
  <Link
    to={item.href}
    title={collapsed ? item.name : undefined}
    data-testid={`coach-sidebar-link-${item.href.replace(/\//g, '-')}`}
    className={`flex items-center gap-3 rounded-lg px-3 py-2 text-sm transition-colors duration-150 ${collapsed ? 'justify-center' : ''} ${
      active ? 'bg-teal-500/15 text-teal-300 font-semibold' : 'text-slate-300 hover:bg-white/5 hover:text-white'
    }`}
  >
    <Icon name={item.icon} className="w-5 h-5 shrink-0" />
    {!collapsed && <span className="truncate">{item.name}</span>}
  </Link>
);

const BrandMark = ({ brand, size = 'h-9 w-9' }) => (
  brand.iconUrl ? (
    <img src={brand.iconUrl} alt="تسيار" className={`${size} shrink-0 rounded-lg object-contain bg-white/5 p-1`} data-testid="coach-brand-icon" />
  ) : (
    <div className={`${size} shrink-0 flex items-center justify-center rounded-lg bg-teal-500/20 text-teal-300`}>
      <Icon name="whistle" className="w-5 h-5" />
    </div>
  )
);

export default function CoachLayout({ children, profile }) {
  const brand = useTasyarBrand();
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [moreSheetOpen, setMoreSheetOpen] = useState(false);
  const [collapsed, setCollapsed] = useState(() => localStorage.getItem(COLLAPSE_KEY) === '1');
  const menuRef = useRef(null);
  const location = useLocation();
  const pageTitle = getCoachRouteTitle(location.pathname);
  const displayName = profile?.full_name_ar || 'المدرب';
  const initial = displayName.trim().charAt(0);
  const photoUrl = profile?.photo_file_id ? `${API}/files/${profile.photo_file_id}` : null;

  useEffect(() => {
    const close = (e) => { if (menuRef.current && !menuRef.current.contains(e.target)) setUserMenuOpen(false); };
    document.addEventListener('mousedown', close);
    return () => document.removeEventListener('mousedown', close);
  }, []);
  useEffect(() => { setMoreSheetOpen(false); }, [location.pathname]);

  const toggleCollapsed = () => {
    setCollapsed((c) => { localStorage.setItem(COLLAPSE_KEY, c ? '0' : '1'); return !c; });
  };

  const handleLogout = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
    delete axios.defaults.headers.common['Authorization'];
    window.location.replace('/login');
  };

  const SidebarBody = ({ forceExpanded }) => {
    const isCollapsed = collapsed && !forceExpanded;
    return (
      <div className="flex h-full flex-col">
        <div className={`flex items-center gap-3 border-b border-white/10 px-4 py-4 ${isCollapsed ? 'justify-center px-2' : ''}`} data-testid="coach-sidebar-brand">
          <BrandMark brand={brand} />
          {!isCollapsed && (
            <div className="min-w-0">
              <div className="truncate text-base font-extrabold" data-testid="coach-sidebar-title">
                <span className="bg-gradient-to-l from-teal-300 to-orange-300 bg-clip-text text-transparent">{brand.name}</span>
              </div>
              <div className="text-[11px] font-medium text-slate-400">بوابة المدرب</div>
            </div>
          )}
        </div>
        <nav className="flex-1 overflow-y-auto px-3 pt-4 pb-16 space-y-4" data-testid="coach-sidebar-nav">
          {coachNavigation.map((group) => (
            <div key={group.key}>
              {!isCollapsed && <div className="mb-1 px-3 text-[11px] font-semibold uppercase tracking-wide text-slate-400">{group.group}</div>}
              <div className="space-y-0.5">
                {group.items.map((item) => (
                  <SidebarLink key={item.href} item={item} active={isActive(location.pathname, item.href)} collapsed={isCollapsed} />
                ))}
              </div>
            </div>
          ))}
        </nav>
        {!forceExpanded && (
          <button
            onClick={toggleCollapsed}
            className="hidden lg:flex items-center justify-center gap-2 border-t border-white/10 py-3 text-slate-400 hover:text-white transition-colors"
            data-testid="coach-sidebar-collapse-btn"
            title={isCollapsed ? 'توسيع القائمة' : 'تصغير القائمة'}
          >
            <Icon name="chevronLeft" className={`w-4 h-4 transition-transform ${isCollapsed ? 'rotate-180' : ''}`} />
            {!isCollapsed && <span className="text-xs">تصغير القائمة</span>}
          </button>
        )}
      </div>
    );
  };

  return (
    <div dir="rtl" className="min-h-screen bg-[#f4f6fa]" style={{ fontFamily: "'Noto Sans Arabic', Arial, sans-serif" }}>
      <aside className={`fixed inset-y-0 right-0 z-40 hidden lg:block bg-[#101b2e] transition-all duration-200 ${collapsed ? 'w-[76px]' : 'w-60'}`} data-testid="coach-sidebar">
        <SidebarBody />
      </aside>

      <div className={`transition-all duration-200 ${collapsed ? 'lg:mr-[76px]' : 'lg:mr-60'}`}>
        <header className="sticky top-0 z-30 border-b border-slate-200/70 bg-white/85 shadow-[0_1px_2px_rgba(0,0,0,0.03)] backdrop-blur-xl" data-testid="coach-topbar">
          <div className="flex h-16 items-center gap-3 px-4 sm:px-6">
            {brand.iconUrl && <img src={brand.iconUrl} alt="تسيار" className="lg:hidden h-7 w-7 rounded-lg object-contain" data-testid="coach-topbar-brand-icon" />}
            <h1 className="text-lg font-black tracking-tight text-slate-900 lg:text-base lg:font-bold" data-testid="coach-topbar-page-title">{pageTitle}</h1>

            <div className="mr-auto flex items-center gap-3">
              <Link to="/" className="hidden sm:block text-xs font-semibold text-slate-500 hover:text-teal-600">الموقع العام</Link>
              <div className="relative" ref={menuRef}>
                <button onClick={() => setUserMenuOpen(!userMenuOpen)} className="flex items-center gap-2 rounded-xl p-1.5 transition-colors hover:bg-slate-100" data-testid="coach-topbar-user-menu-btn">
                  {photoUrl ? (
                    <img src={photoUrl} alt="" className="h-9 w-9 rounded-xl object-cover ring-2 ring-teal-500/10" />
                  ) : (
                    <div className="flex h-9 w-9 items-center justify-center rounded-xl bg-gradient-to-br from-[#101b2e] to-[#1a2d4a] text-sm font-black text-white shadow-sm">{initial}</div>
                  )}
                  <div className="hidden sm:block text-right">
                    <div className="text-sm font-bold text-slate-800 leading-tight" data-testid="coach-topbar-user-name">{displayName}</div>
                    <div className="text-[11px] font-semibold text-teal-600 leading-tight">مدرب مستقل</div>
                  </div>
                  <Icon name="chevronDown" className="hidden sm:block w-3.5 h-3.5 text-slate-400" />
                </button>
                {userMenuOpen && (
                  <div className="absolute left-0 mt-2 w-48 rounded-2xl border border-slate-200/80 bg-white p-1.5 shadow-2xl" data-testid="coach-user-menu-dropdown">
                    <Link to="/coach/profile" onClick={() => setUserMenuOpen(false)} className="flex items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-slate-700 hover:bg-teal-50 hover:text-teal-800">
                      <Icon name="cog" className="w-4 h-4 text-teal-500" /> ملفي المهني
                    </Link>
                    <button onClick={handleLogout} data-testid="coach-logout-btn" className="flex w-full items-center gap-2.5 rounded-xl px-3 py-2.5 text-sm font-semibold text-red-600 hover:bg-red-50">
                      <Icon name="logout" className="w-4 h-4" /> تسجيل الخروج
                    </button>
                  </div>
                )}
              </div>
              <NotificationBell user={!!localStorage.getItem('token')} align="left" dropdownDirection="down" />
            </div>
          </div>
        </header>

        <main className="p-4 pb-28 sm:p-6 lg:pb-6" data-testid="coach-main-content">{children}</main>
        <CoachFooter />
      </div>

      <CoachMobileBottomNav onMore={() => setMoreSheetOpen(true)} />
      {moreSheetOpen && (
        <CoachMobileMoreSheet
          onClose={() => setMoreSheetOpen(false)}
          onLogout={handleLogout}
          onOpenSupport={() => window.dispatchEvent(new CustomEvent('tasyar:open-chat-widget', { detail: { mode: 'ticket' } }))}
        />
      )}
    </div>
  );
}
